import { QuestionCircleOutlined } from '@ant-design/icons'
import { getOperationName } from '@apollo/client/utilities'
import { useToggle } from 'ahooks'
import { Checkbox, Form, Input, Modal, Radio, Select, Tooltip } from 'antd'
import { FormInstance } from 'antd/lib/form'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import {
  GetViewerCreatedGroupsDocument,
  GetViewerDocument,
  GetViewerFollowingGroupsDocument,
  Group,
  GroupPrivateSetting,
  Role,
  WhoCanPost,
  useGetGroupCategoriesQuery,
  useGetViewerQuery,
  useGroupCreateMutation,
  useGroupInviteMutation,
  useGroupUpdateMutation,
} from '@/generated/graphql'
import { useIsGroupAdmin } from '@/modules/group/group.hooks'
import { DepartmentWithSearch } from '@/modules/group/invite-user'

import s from './group-create-edit.module.less'

interface GroupCreateEditProps {
  group?: Group
}

interface IdsState {
  departmentIds: number[]
  userIds: number[]
}

interface GroupCreateEditFormProps extends GroupCreateEditProps {
  form: FormInstance
  inviteeIds: IdsState
  onUpdateIds: (inviteeIds: IdsState) => void
}

const GroupCreateEditForm: React.FC<GroupCreateEditFormProps> = ({ group, form, inviteeIds, onUpdateIds }) => {
  const [t] = useTranslation()
  const { data: viewerData } = useGetViewerQuery()
  const { data: groupCategoryData } = useGetGroupCategoriesQuery()

  const isEditMode = !!group

  const isSystemAdmin = viewerData!.viewer.roles.find(
    ({ role }) => role === Role.RootAdmin || role === Role.SystemAdmin
  )

  const { isGroupAdmin } = useIsGroupAdmin(group)

  React.useEffect(() => {
    form.resetFields()
  }, [])

  const initialValues = group
    ? {
        name: group.name,
        description: group.description,
        groupCategoryId: group.groupCategoryId,
        settings: [
          group.isOfficial && 'isOfficial',
          !group.canUnfollow && 'cannotUnfollow',
          !group.canPostAnonymously && 'canNotPostAnonymously',
        ].filter(Boolean),
        privateSetting: group.privateSetting,
        whoCanPost: group.whoCanPost,
      }
    : {
        name: '',
        description: '',
        groupCategoryId: '',
        settings: [],
        whoCanPost: WhoCanPost.Member,
        privateSetting: GroupPrivateSetting.Normal,
        invitedUser: [],
      }

  const groupCategories = groupCategoryData ? groupCategoryData.groupCategories : []

  const groupCategoryFormItem = (isGroupAdmin || !group) && (
    <Form.Item name='groupCategoryId' label={t('group.category')} rules={[{ required: true }]}>
      <Select placeholder=''>
        {groupCategories.map((g: any) => (
          <Select.Option key={g.groupCategoryId} value={g.groupCategoryId}>
            {t(`groups.category.${g.groupCategoryId}`) + ' - ' + t(`groups.catDes.${g.groupCategoryId}`)}
          </Select.Option>
        ))}
      </Select>
    </Form.Item>
  )

  return (
    <div className={s.form}>
      <Form
        form={form}
        labelCol={{ span: 6 }}
        wrapperCol={{ offset: 1 }}
        validateMessages={{
          required: t('group.createValidation'),
        }}
        requiredMark={!isEditMode}
        initialValues={initialValues}
      >
        <Form.Item name='name' label={t('group.name')} rules={[{ required: true }]}>
          <Input allowClear />
        </Form.Item>

        <Form.Item name='description' label={t('group.info')}>
          <Input.TextArea allowClear showCount maxLength={100} autoSize={{ minRows: 3, maxRows: 5 }} />
        </Form.Item>

        {groupCategoryFormItem}

        {isSystemAdmin && (
          <Form.Item name='settings' label={t('group.settings')}>
            <Checkbox.Group className={s.checkboxGroup}>
              <Checkbox value='isOfficial'>{t('group.officialGroup')}</Checkbox>
              <Checkbox value='cannotUnfollow'>{t('group.cannotUnfollow')}</Checkbox>
              <Checkbox value='canNotPostAnonymously'>{t('group.notSupportAnonymous')}</Checkbox>
            </Checkbox.Group>
          </Form.Item>
        )}

        <Form.Item
          name='privateSetting'
          label={
            <div>
              <Tooltip title={t('group.create.privateSetting.info')}>
                <QuestionCircleOutlined style={{ marginRight: 8, cursor: 'pointer' }} />
              </Tooltip>
              <span>{t('group.privateSetting.privateSetting')}</span>
            </div>
          }
        >
          <Radio.Group onChange={(e) => e.target.value}>
            <Radio value={GroupPrivateSetting.Normal}>{t('group.privateSetting.openedGroup')}</Radio>
            <Radio value={GroupPrivateSetting.Closed}>{t('group.privateSetting.closedGroup')}</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item name='whoCanPost' label={t('group.postSettings')}>
          <Radio.Group onChange={(e) => e.target.value}>
            <Radio value={WhoCanPost.Member}>{t('group.groupMember')}</Radio>
            <Radio value={WhoCanPost.Admin}>{t('group.onlyAdmin')}</Radio>
            <Radio value={WhoCanPost.Anyone}>{t('group.anyone')}</Radio>
          </Radio.Group>
        </Form.Item>

        {!isEditMode && (
          <Form.Item name='invitedUser' label={t('group.addUser')}>
            <DepartmentWithSearch ids={inviteeIds} onUpdateIds={onUpdateIds} />
          </Form.Item>
        )}
      </Form>
    </div>
  )
}

export const GroupCreateEdit: React.FC<GroupCreateEditProps> = ({ group, children }) => {
  const [t] = useTranslation()
  const history = useHistory()
  const [visible, { toggle }] = useToggle()
  const [invite] = useGroupInviteMutation()
  const [ids, setIds] = React.useState<IdsState>({
    departmentIds: [],
    userIds: [],
  })

  const refetchQueries = [
    getOperationName(GetViewerCreatedGroupsDocument)!,
    getOperationName(GetViewerFollowingGroupsDocument)!,
    getOperationName(GetViewerDocument)!,
  ]

  const [createGroup, { loading: createLoading }] = useGroupCreateMutation({
    refetchQueries,
  })

  const [updateGroup, { loading: updateLoading }] = useGroupUpdateMutation({
    refetchQueries,
  })

  const isEditMode = !!group

  const [form] = Form.useForm()

  const onOk = async () => {
    const values = await form.validateFields()
    // 非系统管理员创建群组时无 settings 属性
    if (!values.settings) {
      values.settings = []
    }

    const groupInfo = {
      name: values.name,
      description: values.description,
      groupCategoryId: values.groupCategoryId || group!.groupCategoryId,
      isOfficial: values.settings.includes('isOfficial'),
      canUnfollow: !values.settings.includes('cannotUnfollow'),
      canPostAnonymously: !values.settings.includes('canNotPostAnonymously'),
      privateSetting: values.privateSetting,
      whoCanPost: values.whoCanPost,
    }

    if (isEditMode) {
      await updateGroup({
        variables: {
          groupId: group!.groupId,
          group: groupInfo,
        },
      })
      toggle()
    } else {
      const { data } = await createGroup({
        variables: {
          group: groupInfo,
        },
      })
      if (data) {
        toggle()
        invite({
          variables: {
            followeeGroupId: data.groupCreate.groupId,
            followerUserIds: ids.userIds,
            departmentIds: ids.departmentIds,
          },
        })
        history.push(`/groups/${data!.groupCreate.groupId}`)
      }
    }
  }

  return (
    <>
      <div onClick={() => toggle()}>{children}</div>
      <Modal
        width={620}
        className={s.modal}
        title={isEditMode ? t('group.edit') : t('groups.createGroup')}
        visible={visible}
        onCancel={() => toggle()}
        onOk={onOk}
        confirmLoading={createLoading || updateLoading}
        destroyOnClose
      >
        <GroupCreateEditForm group={group} form={form} inviteeIds={ids} onUpdateIds={(ids) => setIds(ids)} />
      </Modal>
    </>
  )
}
